import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Package,
  Plus,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  BarChart3,
  RefreshCw,
  Filter
} from 'lucide-react';

import { useProducts } from './Utils/useProducts';
import ProductTable from './Components/ProductTable';
import CreateProductButton from './Buttons/CreateProductButton';
import { ProductFormModal } from './Modals/ProductFormModal';
import { DeleteProductModal } from './Modals/DeleteProductModal';

const ProductsSection = ({ clientId }) => {
  const {
    // Data
    products,
    allProducts,
    availableItems,
    categories,
    isLoading,
    error,
    
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    startIndex,
    endIndex,
    
    // Actions
    goToPage,
    changeItemsPerPage,
    refresh,
    fetchAvailableItems
  } = useProducts(clientId);

  // Local state for editing and deleting
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);

  const handleProductCreated = (newProduct) => {
    refresh();
  };

  const handleProductEdit = (product) => {
    setEditingProduct(product);
  };

  const handleProductUpdated = (updatedProduct) => {
    setEditingProduct(null);
    refresh();
  };

  const handleProductDelete = (product) => {
    setDeletingProduct(product);
  };

  const handleProductDeleted = () => {
    setDeletingProduct(null);
    refresh();
  };

  // Stats calculation from products
  const getStatsFromProducts = () => {
    const productsArray = Array.isArray(allProducts) ? allProducts : [];
    
    if (productsArray.length === 0) {
      return {
        total: 0,
        active: 0,
        inactive: 0,
        lowStock: 0,
        outOfStock: 0
      };
    }

    return {
      total: productsArray.length,
      active: productsArray.filter(p => p.active === true || p.active === 'true').length,
      inactive: productsArray.filter(p => p.active === false || p.active === 'false').length,
      lowStock: productsArray.filter(p => {
        const stock = parseInt(p.stock_quantity) || 0;
        const minStock = parseInt(p.min_stock) || 0;
        return stock > 0 && stock <= minStock;
      }).length,
      outOfStock: productsArray.filter(p => {
        const stock = parseInt(p.stock_quantity) || 0;
        return stock === 0;
      }).length
    };
  };

  const calculatedStats = getStatsFromProducts();
  const displayStats = calculatedStats;

  if (!clientId) {
    return (
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2">
              Cliente no seleccionado
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Selecciona un cliente para gestionar sus productos.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
            <Package className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Gestión de Productos
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Administra el catálogo de productos del cliente
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isLoading}
            className="border-slate-200/60 hover:bg-orange-50 hover:border-orange-300"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>

          <CreateProductButton
            clientId={clientId}
            availableItems={availableItems}
            categories={categories}
            onProductCreated={handleProductCreated}
            fetchAvailableItems={fetchAvailableItems}
            size="default"
            variant="default"
            showIcon={true}
            showText={true}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Productos
                </p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {displayStats.total || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Package className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Productos Activos
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {displayStats.active || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Productos Inactivos
                </p>
                <p className="text-2xl font-bold text-slate-500 dark:text-slate-400">
                  {displayStats.inactive || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-slate-500 dark:text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Stock Bajo
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {displayStats.lowStock || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200/60 dark:border-slate-700/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Sin Stock
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {displayStats.outOfStock || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Table */}
      <ProductTable
        products={products}
        categories={categories}
        isLoading={isLoading}
        error={error}
        onEdit={handleProductEdit}
        onDelete={handleProductDelete}
        onRefresh={refresh}
        // Pagination props
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        startIndex={startIndex}
        endIndex={endIndex}
        onPageChange={goToPage}
        onItemsPerPageChange={changeItemsPerPage}
      />

      {/* Edit Product Modal */}
      <ProductFormModal
        isOpen={!!editingProduct}
        onClose={() => setEditingProduct(null)}
        onSuccess={handleProductUpdated}
        mode="edit"
        product={editingProduct}
        title={`Editar Producto: ${editingProduct?.name || 'Sin nombre'}`}
        clientId={clientId}
        availableItems={availableItems}
        categories={categories}
        fetchAvailableItems={fetchAvailableItems}
      />

      {/* Delete Product Modal */}
      <DeleteProductModal
        isOpen={!!deletingProduct}
        onClose={() => setDeletingProduct(null)}
        onConfirm={handleProductDeleted}
        product={deletingProduct}
        clientId={clientId}
      />
    </div>
  );
};

export default ProductsSection; 